<template>
  <div id="map" class="map-container"></div>
</template>

<script setup>
import { defineProps, defineEmits, onMounted, nextTick, watch } from 'vue';
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";

const emits = defineEmits(["setLocate"]);

const props = defineProps({
  latitude: {
    type: Number,
    default: 6.3644,
  },
  longitude: {
    type: Number,
    default: 2.4221,
  },
  zoom: {
    type: Number,
    default: 13,
  },
  parent: {
    type: String,
    default: ''
  },
  id: {
    type: String,
    default: ''
  },
  address: {
    type: String,
    default: ''
  }
});

watch(() => props.id, (value) => {
  if(value){
    initiateMap()
  }
});

let mapInstance = null;

async function initiateMap() {
  await nextTick();

  if (mapInstance) {
    mapInstance.remove();
    mapInstance = null;
  }

  const reverseGeocode = async (lat, lng) => {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`;
    const response = await fetch(url);
    const data = await response.json();
    return data.display_name || '';
  };

  let latitude = !isNaN(props.latitude) ? props.latitude : 6.3644;
  let longitude = !isNaN(props.longitude) ? props.longitude : 2.4221;
  let address = props.address ?? await reverseGeocode(latitude, longitude);

  var customIcon = L.divIcon({
    html: '<i class="fas fa-map-marker-alt text-danger fs-2x"></i>',
    iconSize: [0, 0],
    iconAnchor: [16, 32],
    popupAnchor: [-5, -32]
  });

  mapInstance = L.map("map").setView([latitude, longitude], props.zoom);

  L.tileLayer("https://b.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(mapInstance);

  const marker = L.marker([latitude, longitude], { icon: customIcon })
    .addTo(mapInstance)
    .bindPopup(`${i18n.global.t('feed.latitude')}: ${latitude}, ${i18n.global.t('feed.longitude')}: ${longitude}<br>${i18n.global.t('feed.address')}: ${address}`)
    .openPopup();

  const geocoder = L.Control.geocoder({
    defaultMarkGeocode: false,
    placeholder: i18n.global.t('map_search'),
    geocoder: L.Control.Geocoder.nominatim()
  })
    .on('markgeocode', async function (e) {
      const center = e.geocode.center;

      mapInstance.setView(center, 16);
      marker.setLatLng(center).update();

      const location = await reverseGeocode(center.lat, center.lng);

      marker.bindPopup(`${i18n.global.t('feed.latitude')}: ${center.lat}, ${i18n.global.t('feed.longitude')}: ${center.lng}<br>${i18n.global.t('feed.address')}: ${location}`).openPopup();
      
      let data = {
        latitude: center.lat,
        longitude: center.lng,
        location: location,
        address: location,
      };
      emits('setLocate', data);
    })
    .addTo(mapInstance);

    mapInstance.on('click', async (e) => {
    const lat = e.latlng.lat;
    const lng = e.latlng.lng;

    const location = await reverseGeocode(lat, lng);

    let data = {
      latitude: lat,
      longitude: lng,
      location: location,
      address: location,
    }
    emits('setLocate', data);

    marker.setLatLng([lat, lng]).update();
    marker.bindPopup(`${i18n.global.t('feed.latitude')}: ${lat}, ${i18n.global.t('feed.longitude')}: ${lng}<br>${i18n.global.t('feed.address')}: ${location}`).openPopup();
  });

  setTimeout(() => {
    mapInstance.invalidateSize();
  }, 100);
}

onMounted(() => {
  initiateMap();
});
</script>

<style scoped>
.map-container {
  height: 300px;
  width: 100%;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  position: relative;
  overflow: hidden;
}
</style>
